import React, { useEffect, useState } from "react";
import { api, formatDetail, fmtDateTime } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, StatusBadge } from "@/components/ui-parts";
import { Send, Copy, Check, Loader2, Trash2, MessageCircle, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function MyAccountCard() {
  const { user, reloadUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [newPass, setNewPass] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setName(user.name || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!current) { toast.error("Aktuelles Passwort ist Pflicht"); return; }
    const payload = { current_password: current };
    if (email && email !== user.email) payload.email = email;
    if (name && name !== (user.name || "")) payload.name = name;
    if (newPass) payload.new_password = newPass;
    if (Object.keys(payload).length === 1) {
      toast.info("Keine Änderungen");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/me", payload);
      toast.success("Konto aktualisiert");
      setCurrent(""); setNewPass("");
      if (reloadUser) await reloadUser();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E6E6E4] rounded-md p-6" data-testid="my-account">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-md bg-[#E4F0E8] flex items-center justify-center">
          <User className="h-5 w-5 text-[#18322B]" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-lg text-[#1A1A1A]">Mein Konto</div>
          <div className="text-sm text-[#5C5E60]">E-Mail, Name und Passwort ändern. Aktuelles Passwort ist zur Bestätigung erforderlich.</div>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Anzeigename</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="account-name"
              className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">E-Mail (Login)</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="account-email"
              className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Aktuelles Passwort <span className="text-[#B31D1D]">*</span></label>
            <input
              type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
              data-testid="account-current-password"
              className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Neues Passwort <span className="text-[#5C5E60]">(optional)</span></label>
            <input
              type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)}
              placeholder="min. 6 Zeichen"
              data-testid="account-new-password"
              className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]"
            />
          </div>
        </div>
        <PrimaryButton type="submit" disabled={busy} data-testid="account-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Speichern
        </PrimaryButton>
      </form>
    </div>
  );
}

export default function TelegramSettings() {
  const [settings, setSettings] = useState(null);
  const [links, setLinks] = useState([]);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [linkCode, setLinkCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/settings"), api.get("/telegram/links")]);
      setSettings(s.data);
      setLinks(l.data);
    } catch (err) {
      // may fail if user lacks permission
    }
  };

  useEffect(() => { load(); }, []);

  const saveToken = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/settings", { telegram_token: token });
      toast.success(token ? "Bot-Token gespeichert – Bot wird gestartet" : "Bot deaktiviert");
      setToken("");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const createLinkCode = async () => {
    try {
      const { data } = await api.post("/telegram/link-code", {});
      setLinkCode(data.code);
    } catch { toast.error("Konnte Code nicht erstellen"); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(linkCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const removeLink = async (l) => {
    if (!window.confirm("Verknüpfung entfernen?")) return;
    await api.delete(`/telegram/links/${l.id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Telegram-Bot"
        subtitle="Belege per Telegram einreichen. Bot-Token verwalten und Chats verknüpfen."
      />

      <div className="px-8 py-8 space-y-6 max-w-3xl">
        <MyAccountCard />
        {/* Telegram Bot Configuration */}
        <div className="bg-white border border-[#E6E6E4] rounded-md p-6" data-testid="telegram-settings">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-[#EAF4FA] flex items-center justify-center">
              <Send className="h-5 w-5 text-[#1A4971]" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg text-[#1A1A1A]">Telegram-Bot</div>
              <div className="text-sm text-[#5C5E60]">
                Belege per Telegram-Chat einreichen. Bot-Token vom BotFather (@BotFather) benötigt.
              </div>
            </div>
            {settings?.telegram_configured
              ? <StatusBadge variant="success">Aktiv</StatusBadge>
              : <StatusBadge variant="neutral">Nicht konfiguriert</StatusBadge>}
          </div>

          {settings?.telegram_configured ? (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-[#5C5E60]">Token: </span>
                <span className="font-mono text-xs">{settings.telegram_token_preview}</span>
              </div>
              <form onSubmit={saveToken} className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Neuen Token setzen (überschreibt)</label>
                  <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
                    placeholder="1234567890:ABC-..."
                    data-testid="telegram-token-input"
                    className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]" />
                </div>
                <PrimaryButton type="submit" disabled={saving || !token}>{saving ? "..." : "Aktualisieren"}</PrimaryButton>
              </form>
              <button
                onClick={async () => {
                  if (!window.confirm("Telegram-Bot deaktivieren?")) return;
                  setSaving(true);
                  try {
                    await api.post("/settings", { telegram_token: "" });
                    toast.success("Deaktiviert");
                    load();
                  } finally { setSaving(false); }
                }}
                className="text-xs text-[#B31D1D] hover:underline"
                data-testid="telegram-disable">
                Bot deaktivieren
              </button>
            </div>
          ) : (
            <form onSubmit={saveToken} className="space-y-3">
              <div className="text-xs text-[#5C5E60] bg-[#F9F9F8] border border-[#E6E6E4] rounded-md p-3 leading-relaxed">
                <b className="text-[#1A1A1A]">Token besorgen:</b> Öffnen Sie Telegram, suchen Sie <b>@BotFather</b>, senden Sie{" "}
                <code className="bg-white px-1.5 py-0.5 rounded">/newbot</code>, wählen Sie Name und Username (muss auf &bdquo;_bot&ldquo; enden).
                Sie erhalten einen Token wie <code className="bg-white px-1.5 py-0.5 rounded">1234:AAH-abc-...</code>. Diesen hier einfügen.
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Bot-Token</label>
                <input type="password" required value={token} onChange={(e) => setToken(e.target.value)}
                  placeholder="1234567890:ABC-..."
                  data-testid="telegram-token-input"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]" />
              </div>
              <PrimaryButton type="submit" disabled={saving} data-testid="telegram-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Bot aktivieren
              </PrimaryButton>
            </form>
          )}
        </div>

        {/* Link Codes */}
        {settings?.telegram_configured && (
          <div className="bg-white border border-[#E6E6E4] rounded-md p-6" data-testid="telegram-links">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-md bg-[#E4F0E8] flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-[#18322B]" strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg text-[#1A1A1A]">Mein Telegram verknüpfen</div>
                <div className="text-sm text-[#5C5E60]">
                  Generieren Sie einen Code und senden Sie <code className="text-xs bg-[#F3F3F1] px-1 py-0.5 rounded">/start CODE</code> an Ihren Bot.
                </div>
              </div>
            </div>

            {linkCode ? (
              <div className="bg-[#E4F0E8] border border-[#18322B]/20 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#18322B] font-semibold">Ihr Code</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="font-mono font-bold text-2xl text-[#18322B] flex-1" data-testid="telegram-link-code">{linkCode}</div>
                  <button onClick={copyCode} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-[#18322B] text-white hover:bg-[#21453B] transition-colors duration-150">
                    {copied ? <><Check className="h-3.5 w-3.5" /> Kopiert</> : <><Copy className="h-3.5 w-3.5" /> Kopieren</>}
                  </button>
                </div>
                <div className="text-xs text-[#1E4620] mt-2">
                  Öffnen Sie Ihren Bot in Telegram und senden Sie: <code className="bg-white px-1.5 py-0.5 rounded font-mono">/start {linkCode}</code>
                </div>
                <button onClick={() => setLinkCode(null)} className="mt-2 text-xs text-[#18322B] underline">Schließen</button>
              </div>
            ) : (
              <SecondaryButton onClick={createLinkCode} data-testid="create-link-code">
                <MessageCircle className="h-4 w-4" /> Verknüpfungscode erzeugen
              </SecondaryButton>
            )}

            {links.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold mb-2">Verknüpfte Chats</div>
                <div className="space-y-1">
                  {links.map((l) => (
                    <div key={l.id} className="flex items-center justify-between px-3 py-2 border border-[#E6E6E4] rounded-md text-sm">
                      <div>
                        <div className="text-[#1A1A1A]">
                          {l.status === "active"
                            ? <>@{l.telegram_username || l.chat_id} <StatusBadge variant="success">Verknüpft</StatusBadge></>
                            : <><span className="font-mono">{l.link_code}</span> <StatusBadge variant="warning">Wartet auf /start</StatusBadge></>}
                        </div>
                        <div className="text-xs text-[#5C5E60]">{fmtDateTime(l.linked_at || l.created_at)}</div>
                      </div>
                      <button onClick={() => removeLink(l)} className="text-xs text-[#B31D1D] hover:bg-[#FCE8E8] px-2 py-1 rounded">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
